import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  iconSize,
  palette,
  Stack,
  StatusChip,
  statusTone,
  TextField,
  Typography,
  fontStacks,
} from '@munifocus/ui';
import type { DocumentRequestDto } from '@munifocus/contracts-gov';
import { useDecideDocumentRequest, useDocumentRequests } from '../lib/document-requests-api';
import { formatDateTime } from '../lib/relative-time';
import { useSubmitValidation } from '../lib/useSubmitValidation';

/**
 * What the property owner has asked for on this file, and the officer's answer.
 *
 * Only `on_request` documents ever reach an officer — a notice the owner was
 * served is released by the portal without asking anyone. So a row awaiting a
 * decision here is always a real judgement call, which is why refusing requires
 * a reason: the owner reads it verbatim, and a refusal they cannot understand
 * is one they cannot challenge.
 */
export function DocumentRequestsPanel({ fileId }: { fileId: string }) {
  const { t, i18n } = useTranslation();
  const requests = useDocumentRequests(fileId);
  const decide = useDecideDocumentRequest(fileId);
  const [refusing, setRefusing] = useState<DocumentRequestDto | null>(null);
  const [reason, setReason] = useState('');
  const v = useSubmitValidation({
    reason: reason.trim() ? null : t('common.validation.required'),
  });
  const closeRefusal = () => {
    setRefusing(null);
    v.reset();
  };

  const open = requests.data?.filter((r) => r.state === 'requested').length ?? 0;

  function confirmRefusal() {
    if (!refusing || !reason.trim()) return;
    decide.mutate(
      { requestId: refusing.id, decision: 'refuse', reason: reason.trim() },
      {
        onSuccess: () => {
          closeRefusal();
          setReason('');
        },
      },
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography
          component="h2"
          sx={{
            fontFamily: fontStacks.display,
            fontWeight: 700,
            fontSize: 16,
            color: palette.text.primary,
          }}
        >
          {t('gov.documentRequests.title')}
        </Typography>
        {open > 0 && (
          <StatusChip
            status={statusTone.documentRequest.requested}
            label={t('gov.documentRequests.open', { count: open })}
          />
        )}
      </Stack>

      {requests.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} thickness={5} sx={{ color: palette.brand.accent }} />
        </Box>
      )}

      {requests.isError && <Alert severity="error">{t('gov.documentRequests.loadError')}</Alert>}

      {requests.data?.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('gov.documentRequests.empty')}
        </Typography>
      )}

      {requests.data?.map((request) => (
        <HairlineCard key={request.id} sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
          >
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Icons.FileText size={iconSize.sm} color={palette.text.muted} />
                <Typography sx={{ fontWeight: 600 }}>
                  {t(`gov.forms.kind.${request.kind}`)}
                </Typography>
                <StatusChip
                  status={statusTone.documentRequest[request.state]}
                  label={t(`gov.documentRequests.state.${request.state}`)}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t('gov.documentRequests.requestedBy', {
                  name: request.requesterName || request.requesterEmail,
                  date: formatDateTime(request.requestedAt, i18n.language),
                })}
              </Typography>

              {request.state !== 'requested' && (
                <Typography variant="caption" color="text.secondary">
                  {/* A null decider is not missing data: it is the policy
                      having released the document rather than a person. */}
                  {request.decidedByName
                    ? t('gov.documentRequests.decidedBy', {
                        name: request.decidedByName,
                        date: formatDateTime(request.decidedAt ?? '', i18n.language),
                      })
                    : t('gov.documentRequests.autoSent')}
                  {request.answeredVersion !== null &&
                    ` · ${t('gov.documentRequests.servedVersion', {
                      version: request.answeredVersion,
                    })}`}
                </Typography>
              )}

              {request.refusalReason && (
                <Typography variant="body2" color="text.secondary">
                  {request.refusalReason}
                </Typography>
              )}
            </Stack>

            {request.state === 'requested' && (
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                  variant="contained"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ requestId: request.id, decision: 'send' })}
                >
                  {t('gov.documentRequests.send')}
                </Button>
                <Button
                  variant="outlined"
                  disabled={decide.isPending}
                  onClick={() => {
                    setRefusing(request);
                    setReason('');
                  }}
                >
                  {t('gov.documentRequests.refuse')}
                </Button>
              </Stack>
            )}
          </Stack>
        </HairlineCard>
      ))}

      {/* A refusal takes a reason, so it takes a dialog rather than a confirm:
          there is something to type, not merely something to agree to. */}
      <Dialog open={!!refusing} onClose={closeRefusal} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.documentRequests.refuseTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('gov.documentRequests.refuseHint')}
            </Typography>
            <TextField
              label={t('gov.documentRequests.refuseLabel')}
              {...v.field('reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={3}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              autoFocus
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRefusal}>{t('gov.documentRequests.cancel')}</Button>
          <Button
            variant="contained"
            // The API rejects a blank reason; pressing this without one says so
            // at the field rather than sending it.
            disabled={decide.isPending}
            onClick={v.guard(confirmRefusal)}
          >
            {t('gov.documentRequests.refuseConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
